import { useContext, useState } from "react";

import { useMutation } from "@apollo/client";
import { IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { FAVOURITES_ICON_SOURCE, MOBILE_APP } from "../../app/constants/constants";
import { MODIFY_RECIPE_DATA } from "../../app/graphql/mutations";
import { UseGetFlag } from "../../app/launchDarkly";
import { setInitialRecipeOpen, setRecipeData } from "../../app/recipeCreation/createRecipe.slice";
import { setRecipe } from "../../app/recipePage/recipePage.slice";
import { useAppDispatch, useAppSelector } from "../../app/store/hooks";
import useCheckHistory from "../../hooks/useCheckHistory";
import useLogRecipeEdit from '../../hooks/useLogRecipeEdit';
import { ScheduleMealPlanIcon } from "../../iconRef/mealPLanIcons";
import { ReactComponent as BackArrow } from "../../svg/backArrow2.svg";
import { ErrorManagementContext } from "../../utils/errorManagement/ErrorManagementProvider";
import { EventLogsEnum } from "../../utils/events";
import { resizeImageIfCloudinary } from "../../utils/images/cloudinaryUtils";
import { recipeE3TransformerQL2FS } from "../../utils/RecipeBackEnd/hasuraFSMigrationTransformers";
import Favorites from "../favourites/favouritesView";
import { TextHeader } from "../typography";
import styles from './recipeBanner.module.css';
import RecipeBannerMenu from "./recipeBannerMenu/recipeBannerMenu";
import ScheduleMealDrawer from "./scheduleMealDrawer/ScheduleMealDrawer";
import { getAdjustedDateTime } from "../mealPlanPage/utils";
import { setSelectedDate } from "../../app/mealPlan/mealPlan.slice";

const RecipeBanner = ({ recipe, image, servings }: { recipe: any, image: string, servings: number }) => {
    const dispatch = useAppDispatch();
    const { enableFavouritesMob } = UseGetFlag();
    const [modifyRecipeData] = useMutation(MODIFY_RECIPE_DATA)
    const recipeData = useAppSelector((state) => state.recipeCreation.recipeData)
    const { cookClubLog } = useContext(ErrorManagementContext)
    const recipeCreatedSource = recipe?.metadata?.createdSource;
    const mobileCreatedRecipe = recipeCreatedSource === MOBILE_APP;
    const [recipeName, setRecipeName] = useState<string>(recipe?.metadata?.name);
    const { logRecipeEdit } = useLogRecipeEdit();
    const goBackOrRedirect = useCheckHistory();
    const [drawer, setDrawer] = useState<boolean>(false);

    const onBack = async () => {
        goBackOrRedirect('/');
        logRecipeEdit(EventLogsEnum.APP_BACK_PRESSED);
        dispatch(setInitialRecipeOpen(false))
    }

    const handleScheduleMealClick = () => {

        logRecipeEdit(EventLogsEnum.ADD_TO_MEAL_PLAN, {
            recipe_name: recipe?.metadata?.name
        })

        const adjustedDate = getAdjustedDateTime();
        dispatch(setSelectedDate(adjustedDate));
        setDrawer(true);
    }

    return (
        <Box className={styles.banner_box}>
            <Box
                className={styles.banner_img}
                style={{ backgroundImage: `url(${resizeImageIfCloudinary(image, 500, -1, -1, -1, true)})` }}
            >
                <Box className={styles.banner_container}>
                    <Box className={styles.topbar_box}>
                        <Box className={styles.banner_back_arrow_box} onClick={onBack}>
                            <BackArrow />
                        </Box>
                        <Grid className={styles.topbar_right_grid} container direction="row">
                            <IconButton className={styles.icon_button} onClick={handleScheduleMealClick}>
                                <ScheduleMealPlanIcon height={22} width={22} />
                            </IconButton>
                            <Grid className={styles.favourite_icon_wrapper}>{(enableFavouritesMob && !mobileCreatedRecipe) ? <Favorites recipeId={recipe.id} size={20} source={FAVOURITES_ICON_SOURCE.RECIPE_BANNER}
                                stroke="none" /> : null}</Grid>
                            <RecipeBannerMenu onBack={onBack} recipe={recipe} recipeName={recipeName}
                                setRecipeName={setRecipeName} />
                        </Grid>
                    </Box>
                    <Box className={styles.recipe_title_container}>
                        <Box className={styles.recipe_categories_tags_container}>
                            {recipe?.metadata?.categories?.map((category: any, index: number) =>
                                <Box className={styles.category_box} key={`${index}${category}`}>
                                    <Typography variant="caption3Bold">{category} •</Typography>
                                </Box>
                            )}
                            {recipe?.metadata?.tags?.map((tag: any, index: number) =>
                                <Box className={styles.tags_box} key={`${index}${tag}`}>
                                    <Typography variant="caption3Bold">{tag}</Typography>
                                </Box>
                            )}
                        </Box>
                        <TextHeader color="#fff" text={recipeName} />
                    </Box>
                </Box>
            </Box>
            <ScheduleMealDrawer drawer={drawer} recipe={recipe} setDrawer={setDrawer} />
        </Box>
    )
}

export default RecipeBanner;
